import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../components/common/Input';
import { LoginUser, GetPermissions } from '../services/accountService';
import { getDefaultRoute, storePermissionsFromApi } from '../utils/PermissionUtils';


const SignIn = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const [errors, setErrors] = useState({
    email: '',
    password: ''
  });

  const [touched, setTouched] = useState({
    email: false,
    password: false
  });

  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState('');

  const validatePassword = (password) => {
    if (!password || password.trim() === '') {
      return 'Password is required';
    }
    return '';
  };

  const validateEmail = (email) => {
    if (!email || email.trim() === '') {
      return 'Email is required';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  useEffect(() => {
    const validateField = (name, value) => {
      if (touched[name]) {
        if (name === 'email') {
          return validateEmail(value);
        } else if (name === 'password') {
          return validatePassword(value);
        }
      }
      return '';
    };

    const newErrors = {
      email: validateField('email', formData.email),
      password: validateField('password', formData.password)
    };

    setErrors(newErrors);
  }, [formData, touched]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));

    if (apiError) {
      setApiError('');
    }

    if (!touched[name]) {
      setTouched(prevState => ({
        ...prevState,
        [name]: true
      }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    if (!touched[name]) {
      setTouched(prevState => ({
        ...prevState,
        [name]: true
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setApiError('');

    setTouched({
      email: true,
      password: true
    });

    const emailError = validateEmail(formData.email);
    const passwordError = validatePassword(formData.password);

    if (emailError || passwordError) {
      setErrors({
        email: emailError,
        password: passwordError
      });
      return;
    }

    setIsLoading(true);

    try {

      const response = await LoginUser(formData.email, formData.password);

      // Store tokens and user data
      if (response.accessToken) {
        localStorage.setItem('authToken', response.accessToken);
        const expiresInSeconds = response.expiresIn ? Number(response.expiresIn) : 43200;
        localStorage.setItem('expiresIn', expiresInSeconds);
      }

      if (response.user) {
        localStorage.setItem('userData', JSON.stringify(response.user));
      }

      const permissionResponse = await GetPermissions();

      if (permissionResponse.success) {
        storePermissionsFromApi(permissionResponse);
      }

      const landingRoute = permissionResponse.success ? getDefaultRoute() : '/dashboard';
      navigate(landingRoute);

    } catch (error) {
      console.error('Login failed:', error);

      if (error.response) {
        const errorMessage = error.response.data?.message ||
          error.response.data?.error ||
          'Login failed. Please check your credentials.';

        setApiError(errorMessage);
      } else if (error.request) {
        setApiError('Network error. Please check your internet connection.');
      } else {
        setApiError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    navigate('/forgot-password');
  };

  return (
    <div className="login-page">
      <div className="welcome-section">
        <div className="welcome-content">
          <h1 className="welcome-title">Welcome to OpenBook</h1>
        </div>
      </div>
      <div className="login-section">
        <div className="login-form-wrapper">
          <div className='rockwell_logo'>
            <img src='../../assets/Rockwell-Logo.jpg' alt='rockwell_logo' />
          </div>
          <div className="form-title-section">
            <h4 className="form-title">Log in</h4>
            <p className="form-subtitle">Login to access your account.</p>
          </div>

          {apiError && (
            <div className="api-error-message">
              {apiError}
            </div>
          )}

          <form onSubmit={handleSubmit} className="login-form">
            <Input
              label={<span>Email <span style={{ color: 'red' }}>*</span></span>}
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              onBlur={handleBlur}
              required
              placeholder="Enter your email"
              error={touched.email && errors.email ? errors.email : ''}
            />

            <Input
              label={<span>Password <span style={{ color: 'red' }}>*</span></span>}
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              onBlur={handleBlur}
              required
              placeholder="Enter your password"
              showForgotPassword={true}
              error={touched.password && errors.password ? errors.password : ''}
              onForgotPassword={handleForgotPassword}
            />

            <button
              type="submit"
              className="login-btn"
              disabled={isLoading || errors.email || errors.password}
            >
              {isLoading ? 'Logging in...' : 'Log in'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SignIn;